import { Box, Button, Flex, Heading, Input, Link, Text } from "@chakra-ui/react";
import { Form } from "@inertiajs/react";
import { forwardRef, useImperativeHandle, useRef } from "react";
import { privacy_path, waitlist_path } from "@/routes";
import { useLandingTheme } from "../Index";
import { useAnalytics } from "@/hooks/useAnalytics";

export const Footer = forwardRef<{ focusInput: () => void }>((_props, ref) => {
  const { theme, isDark } = useLandingTheme();
  const TRACK = useAnalytics();
  const emailInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focusInput: () => {
      emailInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => emailInputRef.current?.focus(), 300);
    },
  }));

  const handleFormSubmit = () => {
    TRACK.BUTTON_CLICKED({ type: "Get Early Access button - footer" });
  };

  return (
    <Box
      as="footer"
      position="relative"
      py={32}
      px={4}
      overflow="hidden"
      display="flex"
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      transition="background-color 0.3s"
    >
      {/* Background Gradient */}
      <Box
        position="absolute"
        bottom={0}
        w="full"
        h="full"
        background={`linear-gradient(to top, ${theme.primary}${isDark ? "1a" : "08"}, transparent, transparent)`}
        pointerEvents="none"
      />

      <Flex position="relative" zIndex={10} maxW="4xl" mx="auto" direction="column" align="center" gap={8}>
        {/* Heading */}
        <Heading
          as="h2"
          fontSize={{ base: "4xl", md: "6xl" }}
          fontWeight="bold"
          color={theme.textPrimary}
          letterSpacing="tight"
        >
          Ready to reclaim your focus?
        </Heading>

        {/* Subtext */}
        <Text fontSize="xl" color={theme.textSecondary} fontWeight="light" maxW="2xl">
          Join the waitlist today and be the first to experience Muro. Peace of mind is just a click away.
        </Text>

        {/* Email Form */}
        <Form action={waitlist_path()} method="post" onSubmit={handleFormSubmit}>
          {({ processing }) => (
            <Flex direction={{ base: "column", sm: "row" }} gap={4} w="full" maxW="md" mt={4}>
              <Input
                ref={emailInputRef}
                name="waitlist[email]"
                type="email"
                placeholder="Enter your email"
                required
                flex={1}
                bg={isDark ? `${theme.surfaceMuted}80` : theme.surface}
                border="1px solid"
                borderColor={theme.border}
                borderRadius="full"
                px={6}
                py={4}
                h="auto"
                color={theme.textPrimary}
                _placeholder={{ color: theme.textSecondary }}
                _focus={{
                  outline: "none",
                  borderColor: theme.primary,
                  boxShadow: `0 0 0 3px ${theme.primary}${isDark ? "33" : "20"}`,
                }}
                transition="all 0.2s"
              />
              <Button
                type="submit"
                bg={theme.primary}
                color="white"
                fontWeight="bold"
                px={8}
                py={4}
                h="auto"
                borderRadius="full"
                boxShadow={`0 4px 20px ${theme.primaryGlow}`}
                _hover={{
                  bg: theme.primaryHover,
                  boxShadow: `0 8px 30px ${theme.primaryGlow}`,
                  transform: "translateY(-2px)",
                }}
                transition="all 0.3s"
                whiteSpace="nowrap"
                disabled={processing}
              >
                {processing ? "Submitting..." : "Get Early Access"}
              </Button>
            </Flex>
          )}
        </Form>

        {/* Footer Links */}
        <Flex
          mt={20}
          pt={8}
          borderTop="1px solid"
          borderColor={theme.border}
          w="full"
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align="center"
          color={theme.textPrimary}
          fontSize="sm"
          gap={4}
        >
          <Text>© {new Date().getFullYear()} Muro. All rights reserved.</Text>
          <Flex gap={6}>
            <Link
              href={privacy_path()}
              color={theme.textPrimary}
              _hover={{ color: theme.primary, textDecoration: "none" }}
              transition="colors 0.2s"
            >
              Privacy Policy
            </Link>
            {/* <Link
              href="#"
              color={theme.textPrimary}
              _hover={{ color: theme.primary, textDecoration: "none" }}
              transition="colors 0.2s"
            >
              Terms of Service
            </Link> */}
            <Link
              href="#"
              color={theme.textPrimary}
              _hover={{ color: theme.primary, textDecoration: "none" }}
              transition="colors 0.2s"
            >
              Twitter
            </Link>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
});

Footer.displayName = "Footer";
